import { createAsyncThunk } from "@reduxjs/toolkit";
import { contractHubService, ContractHubListParams } from "./ContractHubService";

/**
 * Fetch the contract hub grid data.
 * Triggered on initial load and whenever the search value changes.
 */
export const fetchContractHubList = createAsyncThunk(
  "contractHub/fetchList",
  async (params: ContractHubListParams, thunkAPI) => {
    try {
      const response = await contractHubService.fetchContractHubList(params);
      if (!response.status) {
        return thunkAPI.rejectWithValue(
          response.message || "Failed to fetch contract hub list",
        );
      }
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "Unknown error");
    }
  },
);

/**
 * Fetch summary stats used to populate the chip counts in the action bar.
 * Triggered on initial load and on manual refresh.
 */
export const fetchContractHubStats = createAsyncThunk(
  "contractHub/fetchStats",
  async (params: ContractHubListParams = {}, thunkAPI) => {
    try {
      const response = await contractHubService.fetchContractHubStats(params);
      if (!response.status) {
        return thunkAPI.rejectWithValue(
          response.message || "Failed to fetch contract hub stats",
        );
      }
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "Unknown error");
    }
  },
);

/**
 * Fetch the summary counters (status + count) used to populate the banner.
 * Triggered on initial load and on manual refresh.
 */
export const fetchContractHubSummary = createAsyncThunk(
  "contractHub/fetchSummary",
  async (params: ContractHubListParams = {}, thunkAPI) => {
    try {
      const response = await contractHubService.fetchContractHubSummary(params);
      if (!response.status) {
        return thunkAPI.rejectWithValue(
          response.message || "Failed to fetch contract hub summary",
        );
      }
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "Unknown error");
    }
  },
);

/**
 * Convenience thunk that dispatches list, stats and summary fetches in parallel.
 * Use this on page load so all requests fire simultaneously.
 */
export const initContractHub = createAsyncThunk(
  "contractHub/init",
  async (params: ContractHubListParams = {}, thunkAPI) => {
    await Promise.all([
      thunkAPI.dispatch(fetchContractHubList(params)),
      thunkAPI.dispatch(fetchContractHubStats(params)),
      thunkAPI.dispatch(fetchContractHubSummary(params)),
    ]);
  },
);
